import { apiFetch, authHeaders } from '../lib/api.js';

// In-memory cache for /api/clients. The list shifts rarely (user-edited
// records, Clerk role changes) so a short TTL is enough to deduplicate the
// dozen+ tab switches a user does in a session. In-flight requests are also
// shared so concurrent mounts of different pages don't fan out.
const CLIENTS_CACHE_TTL_MS = 60_000;
const clientsCache = new Map(); // key → { value, expiresAt }
const clientsInFlight = new Map(); // key → Promise

function clientsCacheKey(params) {
  const ordered = Object.keys(params || {})
    .sort()
    .reduce((acc, k) => {
      acc[k] = params[k];
      return acc;
    }, {});
  return JSON.stringify(ordered);
}

export function clearClientsCache() {
  clientsCache.clear();
  clientsInFlight.clear();
}

export async function fetchClients(token, params = {}) {
  const key = clientsCacheKey(params);
  const cached = clientsCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  const inFlight = clientsInFlight.get(key);
  if (inFlight) return inFlight;

  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  ).toString();
  const promise = apiFetch(`/api/clients${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(token),
  })
    .then((value) => {
      clientsCache.set(key, { value, expiresAt: Date.now() + CLIENTS_CACHE_TTL_MS });
      return value;
    })
    .finally(() => {
      clientsInFlight.delete(key);
    });
  clientsInFlight.set(key, promise);
  return promise;
}

export async function fetchClientListKpis(token) {
  return apiFetch('/api/clients/list-kpis', { headers: authHeaders(token) });
}

export async function fetchClient(token, id) {
  return apiFetch(`/api/clients/${id}`, { headers: authHeaders(token) });
}

export async function createClient(token, body) {
  const result = await apiFetch('/api/clients', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  clearClientsCache();
  return result;
}

export async function patchClient(token, id, body) {
  const result = await apiFetch(`/api/clients/${id}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  clearClientsCache();
  return result;
}

export async function fetchClientPerformance(token, id, marketplace) {
  const qs = marketplace ? `?marketplace=${encodeURIComponent(marketplace)}` : '';
  return apiFetch(`/api/clients/${id}/performance${qs}`, { headers: authHeaders(token) });
}

export async function fetchClientAccountHealth(token, id) {
  return apiFetch(`/api/clients/${id}/account-health`, { headers: authHeaders(token) });
}

export async function fetchClientTasks(token, id) {
  return apiFetch(`/api/clients/${id}/tasks`, { headers: authHeaders(token) });
}

export async function fetchClientNotes(token, id) {
  return apiFetch(`/api/clients/${id}/notes`, { headers: authHeaders(token) });
}

export async function postClientNote(token, id, text) {
  return apiFetch(`/api/clients/${id}/notes`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ text }),
  });
}

export async function fetchCallNotes(token, id) {
  return apiFetch(`/api/clients/${id}/call-notes`, { headers: authHeaders(token) });
}

export async function fetchEmailStats(token, id) {
  return apiFetch(`/api/clients/${id}/email-stats`, { headers: authHeaders(token) });
}

export async function fetchClientContacts(token, id) {
  return apiFetch(`/api/clients/${id}/contacts`, { headers: authHeaders(token) });
}

export async function postClientContact(token, id, body) {
  return apiFetch(`/api/clients/${id}/contacts`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
}

export async function patchClientContact(token, id, contactId, body) {
  return apiFetch(`/api/clients/${id}/contacts/${contactId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
}

export async function deleteClientContact(token, id, contactId) {
  return apiFetch(`/api/clients/${id}/contacts/${contactId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export async function fetchBrandProfile(token, id) {
  return apiFetch(`/api/clients/${id}/brand-profile`, { headers: authHeaders(token) });
}

export async function patchBrandProfile(token, id, body) {
  return apiFetch(`/api/clients/${id}/brand-profile`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
}

export async function postBrandProfileNote(token, id, text) {
  return apiFetch(`/api/clients/${id}/brand-profile/notes`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ text }),
  });
}

export async function fetchBrandProfileDraft(token, id) {
  return apiFetch(`/api/clients/${id}/brand-profile/auto-draft`, {
    method: 'POST',
    headers: authHeaders(token),
  });
}

export async function fetchIntelligenceProposals(token, id) {
  return apiFetch(`/api/clients/${id}/intelligence-proposals`, { headers: authHeaders(token) });
}

export async function patchIntelligenceProposal(token, clientId, proposalId, body) {
  return apiFetch(`/api/clients/${clientId}/intelligence-proposals/${proposalId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
}

export async function runIntelligenceScan(token, id) {
  return apiFetch(`/api/clients/${id}/intelligence-scan`, {
    method: 'POST',
    headers: authHeaders(token),
  });
}

export async function fetchProfitability(token, clientId, marketplace) {
  const qs = marketplace ? `?marketplace=${encodeURIComponent(marketplace)}` : '';
  return apiFetch(`/api/profitability/${clientId}${qs}`, { headers: authHeaders(token) });
}

export async function extractClientTone(token, clientId, marketplace = 'amazon_uk') {
  return apiFetch(`/api/clients/${clientId}/extract-tone`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ marketplace }),
  });
}

export async function fetchPpcStrategies(token, clientId) {
  return apiFetch(`/api/clients/${clientId}/ppc-strategies`, { headers: authHeaders(token) });
}

export async function generatePpcStrategies(token, clientId) {
  return apiFetch(`/api/clients/${clientId}/ppc-strategies/generate`, {
    method: 'POST',
    headers: authHeaders(token),
  });
}

export async function patchPpcStrategy(token, clientId, strategyId, body) {
  return apiFetch(`/api/clients/${clientId}/ppc-strategies/${strategyId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
}
